import type { SubmitTokenUnion } from '@/components/escript/Token/types';

import type { SubmitPrescriptionResponse } from '@/hooks/escript/useSubmitPrescription';
import { useSubmitPrescription } from '@/hooks/escript/useSubmitPrescription';
import { Logger } from '@/utils/logger';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { SubmitErrorHandler } from 'react-hook-form';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

const logger = new Logger('escript onboarding');

export type OnboardingStates = 'SUBMIT_TOKEN' | SubmitPrescriptionResponse['onboardState'];

export interface EscriptOnboardingContextType {
  onboardingState: OnboardingStates;
  setOnboardingState: React.Dispatch<React.SetStateAction<OnboardingStates>>;
  resetAndSubmitNewToken: () => void;
  toggleModal: () => void;
  setShowSubmitTokenModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmitTokenModal: boolean;
  submitToken: () => void;
  canSubmitToken: boolean;
  onboardingTokenIsPending: boolean;
}

export interface SubmitTokenWithAgreementData {
  tokenInput: string;
  agreementCheckbox: boolean;
}

export const EscriptOnboardingContext = createContext<EscriptOnboardingContextType | undefined>(undefined);

type EscriptOnboardingProviderProps = {
  children: React.ReactNode;
  initialFormValues?: Partial<SubmitTokenUnion>;
  initialShowSubmitTokenModal?: boolean;
};

export const EscriptOnboardingProvider = ({
  children,
  initialFormValues,
  initialShowSubmitTokenModal = false,
}: EscriptOnboardingProviderProps) => {
  const [onboardingState, setOnboardingState] = useState<OnboardingStates>('SUBMIT_TOKEN');
  const [showSubmitTokenModal, setShowSubmitTokenModal] = useState<boolean>(initialShowSubmitTokenModal);
  const { submitPrescriptionMutate, isPending: onboardingTokenIsPending } = useSubmitPrescription();
  const form = useForm<SubmitTokenWithAgreementData>({
    defaultValues: {
      tokenInput: '',
      agreementCheckbox: false,
      ...(initialFormValues ?? {}),
    },
  });
  const { reset, control, handleSubmit, setError } = form;
  const isAgreementChecked = useWatch({ control, name: 'agreementCheckbox' });
  const tokenValue = useWatch({ control, name: 'tokenInput' });
  const canSubmitToken = !!isAgreementChecked && !!tokenValue?.trim();

  const handleProcessTokenSuccess = (data: SubmitPrescriptionResponse) => {
    switch (data.onboardState) {
      case 'DUPLICATE_TOKEN_PENDING_INVITE':
        setOnboardingState('DUPLICATE_TOKEN_PENDING_INVITE');
        return;

      case 'SCRIPT_VALID':
        setOnboardingState('SCRIPT_VALID');
        return;

      case 'DUPLICATE_TOKEN_EXISTING_ACCOUNT':
        setOnboardingState('DUPLICATE_TOKEN_EXISTING_ACCOUNT');
        return;

      case 'INVALID_TOKEN_PENDING_INVITE':
        setOnboardingState('INVALID_TOKEN_PENDING_INVITE');
        return;

      case 'SCRIPT_UNSUPPORTED':
        setOnboardingState('SCRIPT_UNSUPPORTED');
        return;

      case 'SCRIPT_AWAITING_REVIEW':
        setOnboardingState('SCRIPT_AWAITING_REVIEW');
        return;

      case 'SCRIPT_VALID_OUT_OF_STOCK':
        setOnboardingState('SCRIPT_VALID_OUT_OF_STOCK');
        return;

      // All other cases fall through to default
      default:
        setError('tokenInput', {
          type: 'custom',
          message: 'Unfortunately, we do not support this prescription ',
        });
        return;
    }
  };

  const handleProcessTokenError = () => {
    const errorMessage = 'Unfortunately, we cannot find a valid prescription under this token';
    logger.error(`Escript - token failed request, ${errorMessage}`);
    setError('tokenInput', {
      type: 'custom',
      message: errorMessage,
    });
  };

  const handleTokenSubmit = (formData: SubmitTokenUnion) => {
    submitPrescriptionMutate(formData.tokenInput, handleProcessTokenSuccess, handleProcessTokenError);
  };

  const onTokenInvalid: SubmitErrorHandler<SubmitTokenUnion> = (errors) => {
    if (errors.tokenInput) {
      logger.error('Escript - token failed input validation');
    }
  };

  const submitToken = handleSubmit(handleTokenSubmit, onTokenInvalid);

  const resetAndSubmitNewToken = useCallback(() => {
    reset();
    setOnboardingState('SUBMIT_TOKEN');
  }, [reset, setOnboardingState]);

  const toggleModal = useCallback(() => {
    setShowSubmitTokenModal((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      onboardingState,
      setOnboardingState,
      setShowSubmitTokenModal,
      showSubmitTokenModal,
      resetAndSubmitNewToken,
      toggleModal,
      onboardingTokenIsPending,
      canSubmitToken,
      submitToken,
    }),
    [
      onboardingState,
      setOnboardingState,
      setShowSubmitTokenModal,
      showSubmitTokenModal,
      resetAndSubmitNewToken,
      toggleModal,
      onboardingTokenIsPending,
      canSubmitToken,
      submitToken,
    ],
  );

  return (
    <EscriptOnboardingContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </EscriptOnboardingContext.Provider>
  );
};

export const useEscriptOnboardingContext = (): EscriptOnboardingContextType => {
  const context = useContext(EscriptOnboardingContext);
  if (!context) {
    throw new Error('useEscriptOnboardingContext must be used within the EscriptOnboarding provider');
  }
  return context;
};
